import React, { useEffect } from "react";
import { Typography, Slider, Checkbox } from "@mui/material";
import { tss } from "tss-react/mui";
import { SkipNext, SkipPrevious } from "@mui/icons-material";
import commonStyles from "styles/common";

var useStyles = tss.create(({theme}) => ({
    root: {
        color: "#283C50",
        fontSize: 14,
        letterSpacing: 0,
        lineHeight: "16px",
        height: 68,
    },
    left: {
        position: "relative",
        display: "inline-flex",
        left: 86,
    },
    right: {
        position: "relative",
        display: "inline-flex",
        left: 99,
    },
    firstRow: {
        display: "inline-flex",
    },
    checkbox: {
        width: 15,
        height: 15,
        marginRight: 6,
    },
    label: {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
    },
    title: {
        fontSize: 14,
    },
    sliderInput: {
        display: "inline-block",
        width: 70,
        outline: 0,
        border: 0,
        color: theme.palette.text.secondary,
        borderBottom: `solid 1px ${theme.palette.divider}`,
        textAlign: "center",
    },
    icon: {
        position: "relative",
        width: 16,
        height: 16,
        verticalAlign: "middle",
        display: "inline-block",
        top: 2,
        cursor: "pointer",
    },

    content: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 35,
        width: "100%",
    },
    slider: {
        width: "85%",
        textAlign: "center",
        height: 24,
    },
    arrowLeft: {
        marginRight: 5,
    },
    arrowRight: {
        marginLeft: 5,
    },
}));

var globalSettings: any = [];

const timeout_freq = 200;
const round = (num: number) => {
    return Math.round((num + Number.EPSILON) * 100) / 100;
};
let beforeChange: any = null;

type IProps = {
    title: string;
    name: string;
    min: number;
    max: number;
    value: any;
    step: number;
    enable: boolean;
    id?: string;
    icon?: any;
    style?: any;
    className?: any;
    handleChange: (name: string, value: any) => void;
    handleCheckbox: (name: string, value: boolean) => void;
};

type IState = {
    update: boolean;
};

function RangeSliderWithInput(props: IProps) {
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    var { id, title, name, min, max, step, enable, icon, style, className, handleChange, handleCheckbox } = props;
    const [update, setUpdate] = React.useState(false);

    let slider: any, leftInputElem: any, rightInputElem: any;
    let intervalId: any;

    if (!id) {
        id = "slider";
    }

    if (typeof globalSettings[name + "-" + id] === "undefined") {
        globalSettings[name + "-" + id] = props.value;
    }
    //console.log('globalSettings:', globalSettings);
    useEffect(() => {
        globalSettings[name + "-" + id] = props.value;
        setUpdate((prevUpate) => !prevUpate);
    }, [id, name, props.value]);

    var value = globalSettings[name + "-" + id];

    const handleSliderChange = (evt: any, newValue: any) => {
        if (!beforeChange) {
            beforeChange = [...newValue];
        }
        if (beforeChange[0] !== newValue[0] && beforeChange[1] !== newValue[1]) {
            return;
        }
        globalSettings[name + "-" + id] = newValue;
        handleChange(name, newValue);
        setUpdate((prevUpate) => !prevUpate);
    };

    const handleInputChange = (evt: any, type: string) => {
        var value = globalSettings[name + "-" + id];
        let newValue = round(parseFloat(evt.target.value));
        //var leftValue = round(parseFloat(leftInputElem.value));
        //var rightValue = round(parseFloat(rightInputElem.value));

        if (type === "min") {
            value[0] = newValue;
        } else if (type === "max") {
            value[1] = newValue;
        }
        globalSettings[name + "-" + id] = value;
        handleChange(name, value);
        setUpdate((prevUpate) => !prevUpate);
    };

    const handleCheckboxChange = (evt: any) => {
        var value = Boolean(evt.target.checked);
        if (slider && leftInputElem && rightInputElem) {
            if (!value) {
                slider.className = cx(classes.content, common.disabledEvents);
                leftInputElem.className = cx(classes.sliderInput, common.disabledEvents);
                rightInputElem.className = cx(classes.sliderInput, common.disabledEvents);
            } else {
                slider.className = classes.content;
                leftInputElem.className = classes.sliderInput;
                rightInputElem.className = classes.sliderInput;
            }
        }

        handleCheckbox(name, value);
    };

    const stopInterval = (evt: any) => {
        clearTimeout(intervalId);
    };

    const increaseValue = (evt: any) => {
        if (leftInputElem && rightInputElem) {
            var oldValue = globalSettings[name + "-" + id];
            let value = round(parseFloat(rightInputElem.value));
            value = value + step;
            if (value <= max) {
                oldValue[1] = value;
                rightInputElem.stepUp();
                handleChange(name, oldValue);
                globalSettings[name + "-" + id] = oldValue;
                setUpdate((prevUpate) => !prevUpate);
                stopInterval(evt);
                intervalId = setTimeout(increaseValue, timeout_freq, evt);
            }
        }
    };

    const decreaseValue = (evt: any) => {
        if (leftInputElem && rightInputElem) {
            var oldValue = globalSettings[name + "-" + id];
            let value = round(parseFloat(leftInputElem.value));
            value = value - step;
            if (value >= min) {
                oldValue[0] = value;
                leftInputElem.stepDown();
                handleChange(name, oldValue);
                globalSettings[name + "-" + id] = oldValue;
                setUpdate((prevUpate) => !prevUpate);
                stopInterval(evt);
                intervalId = setTimeout(decreaseValue, timeout_freq, evt);
            }
        }
    };

    const handleChangeCommitted = () => {
        beforeChange = null;
    };

    return (
        <div style={{ ...style }} className={[classes.root, className].join(" ")}>
            <div className={classes.firstRow} style={{ userSelect: update ? "none" : "none" }}>
                {icon ? icon : ""}
                <Checkbox className={classes.checkbox} size="small" checked={enable} onChange={handleCheckboxChange} />
                <div className={classes.label}>
                    <Typography className={classes.title}>{title}</Typography>
                </div>
                <div className={classes.left}>
                    <input
                        ref={(elem: any) => (leftInputElem = elem)}
                        name={"input-" + name}
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={Math.trunc(value[0])}
                        className={enable ? classes.sliderInput : cx(classes.sliderInput, common.disabledEvents)}
                        onChange={(evt) => handleInputChange(evt, "min")}
                    />
                </div>
                <div className={classes.right}>
                    <input
                        ref={(elem: any) => (rightInputElem = elem)}
                        name={"input-" + name}
                        type="number"
                        min={min}
                        max={max}
                        step={step}
                        value={Math.trunc(value[1])}
                        className={enable ? classes.sliderInput : cx(classes.sliderInput, common.disabledEvents)}
                        onChange={(evt) => handleInputChange(evt, "max")}
                    />
                </div>
            </div>
            <div
                className={
                    enable ? classes.content : enable ? classes.sliderInput : cx(classes.content, common.disabledEvents)
                }
                ref={(elem: any) => (slider = elem)}
            >
                <div className={classes.arrowLeft}>
                    <SkipPrevious
                        className={classes.icon}
                        onMouseDown={decreaseValue}
                        onMouseUp={stopInterval}
                        onMouseOut={stopInterval}
                    />
                </div>
                <div className={classes.slider}>
                    <Slider
                        key={`slider-${name}`}
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={handleSliderChange}
                        onChangeCommitted={handleChangeCommitted}
                        aria-labelledby="continuous-slider"
                    />
                </div>
                <div className={classes.arrowRight}>
                    <SkipNext
                        className={classes.icon}
                        onMouseDown={increaseValue}
                        onMouseUp={stopInterval}
                        onMouseOut={stopInterval}
                    />
                </div>
            </div>
        </div>
    );
}

export default class RangeSliderInput extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            update: false,
        };
    }

    updateSlider(value: number) {
        if (typeof globalSettings[this.props.name + "-" + this.props.id] !== "undefined") {
            globalSettings[this.props.name + "-" + this.props.id] = value;
        }
        this.setState({ update: !this.state.update });
    }

    render() {
        return <RangeSliderWithInput {...this.props} />;
    }
}
